/**
 * GRANT / REVOKE structure extractor — the grammar-backed replacement for the
 * removed regex parked-grant parser (VDE-2 Slice 1). Pure, regex-free: every
 * structural decision comes from the shared keyword-consumption primitives
 * (`shared.ts`), `readQualifiedName` / `readIdentifier`, and the top-level
 * scanner — never regex (project rule VQE-D10). Low-level lexical helpers live
 * in `grant.parse.ts`; this module is the clause orchestration + entry points.
 *
 * GRANT/REVOKE is seed-only: the inline `[Edit Privileges]` popover builds SQL
 * from form values and replaces the statement text. There is no structural
 * apply path, so the arm carries the pre-parsed seed (`parsed`) plus the
 * statement range, mirroring the byte-range posture of the other arms.
 *
 * Captured shape (best-effort, engine-spanning):
 *   GRANT  <priv [, …]> [ON [<CLASS::>][<KIND>] <obj>] TO   <princ [, …]>
 *          [WITH {GRANT|ADMIN} OPTION]
 *   REVOKE [GRANT OPTION FOR] <priv [, …]> [ON …] FROM <princ [, …]> [CASCADE]
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F2.
 */

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import { scanTopLevelKeywords, type TopKeyword } from '../../structure/scanner';
import type { DdlStructure, ParsedBuffer } from '../../types';

import { skipWsCommentsLocal, tryConsumeKeyword } from './shared';
import type { ParsedGrantPrincipal, ParsedParkedGrant } from './grantTypes';
import {
  type Span,
  normalizePrivilege,
  parseObject,
  parsePrincipal,
  splitListByTopCommas,
} from './grant.parse';

export type {
  ParsedGrantAction,
  ParsedGrantObject,
  ParsedGrantObjectKind,
  ParsedGrantPrincipal,
  ParsedGrantPrincipalKind,
  ParsedParkedGrant,
} from './grantTypes';

/** Default options for the test/string entry shell — sqlite accepts all
 *  three quoted-identifier styles (`[ ]`, backticks, `" "`), so an engine-
 *  agnostic parked statement parses regardless of source dialect. */
const STRING_SHELL_OPTIONS: ParseOptions = { engine: 'sqlite' };

/** `WITH … OPTION` qualifiers we recognise as a trailing grant/admin option
 *  phrase (the documented set at the file header). A top-level `WITH` only
 *  terminates the grantee list when followed by one of these + `OPTION`.
 *
 *  This is a deliberate *superset* of the removed regex parser, not a mirror
 *  of it: that parser only matched ` WITH GRANT OPTION` / ` WITH ADMIN
 *  OPTION`, so for the valid Oracle form `WITH HIERARCHY OPTION` it left
 *  `withGrantOption` unset and kept the text in the grantee list. Recognising
 *  `HIERARCHY` here is the intended correct behavior (real Oracle syntax). */
const WITH_OPTION_QUALIFIERS: ReadonlySet<string> = new Set([
  'GRANT',
  'ADMIN',
  'HIERARCHY',
]);

function firstKeyword(keywords: readonly TopKeyword[], upper: string): TopKeyword | null {
  for (const kw of keywords) if (kw.upper === upper) return kw;
  return null;
}

/** True when nothing but trailing whitespace / comments / a single `;`
 *  separates `from` from `end` — i.e. the candidate keyword is the LAST
 *  top-level token in the statement. */
function isAtStatementTail(
  source: string,
  from: number,
  end: number,
  options: ParseOptions,
): boolean {
  let p = skipWsCommentsLocal(source, from, end, options);
  if (p < end && source[p] === ';') p = skipWsCommentsLocal(source, p + 1, end, options);
  return p >= end;
}

/** True when at least one non-whitespace char sits in [from, to) — i.e. a
 *  grantee precedes the candidate keyword. The removed regex parser only
 *  matched the trailing-option phrases with a *leading space* inside the
 *  grantee text, so a `WITH …`/`CASCADE` that is itself the first grantee
 *  token (nothing before it) was a principal, never the option. */
function hasGranteeBefore(source: string, from: number, to: number): boolean {
  for (let i = from; i < to; i++) {
    const c = source[i];
    if (c !== ' ' && c !== '\t' && c !== '\n' && c !== '\r') return true;
  }
  return false;
}

/** Locate the byte offset where the grantee list ends: either `end`, or the
 *  start of a *confirmed* trailing-option phrase. A bare `WITH`/`CASCADE`
 *  that does not form a valid phrase stays part of the grantee list. The
 *  recognised trailing-option set is a deliberate superset of the removed
 *  regex parser (which only truncated on the exact phrases ` WITH GRANT
 *  OPTION` / ` WITH ADMIN OPTION` / ` CASCADE`): it additionally recognises
 *  Oracle `WITH HIERARCHY OPTION` and `CASCADE CONSTRAINTS`. The leading-
 *  space / grantee-before requirement is faithfully preserved. */
function findTrailingOption(
  source: string,
  tailKws: readonly TopKeyword[],
  granteeStart: number,
  end: number,
  options: ParseOptions,
): { granteeEnd: number; withGrantOption: boolean; cascade: boolean } {
  for (let idx = 0; idx < tailKws.length; idx++) {
    const kw = tailKws[idx]!;
    // The phrase must follow at least one grantee — matches the old parser's
    // leading-space requirement (a leading `WITH`/`CASCADE` is a principal).
    if (!hasGranteeBefore(source, granteeStart, kw.start)) continue;
    if (kw.upper === 'WITH') {
      // `WITH <GRANT|ADMIN|HIERARCHY> OPTION` — confirm via the next two
      // top-level keywords rather than breaking on the bare `WITH`.
      const q = tailKws[idx + 1];
      const opt = tailKws[idx + 2];
      if (q && WITH_OPTION_QUALIFIERS.has(q.upper) && opt && opt.upper === 'OPTION') {
        return { granteeEnd: kw.start, withGrantOption: true, cascade: false };
      }
      continue;
    }
    if (kw.upper === 'CASCADE') {
      // Terminal when `CASCADE` is the last top-level token, OR when the only
      // top-level keyword after it is Oracle's `CONSTRAINTS` and that
      // `CONSTRAINTS` is itself at statement tail (`REVOKE … CASCADE
      // CONSTRAINTS`). The removed regex parser matched the ` CASCADE`
      // substring, so it produced cascade:true with grantees up to `CASCADE`
      // for that real-world Oracle form too — preserve that.
      if (isAtStatementTail(source, kw.end, end, options)) {
        return { granteeEnd: kw.start, withGrantOption: false, cascade: true };
      }
      const nextKw = tailKws[idx + 1];
      if (
        nextKw &&
        nextKw.upper === 'CONSTRAINTS' &&
        isAtStatementTail(source, nextKw.end, end, options)
      ) {
        return { granteeEnd: kw.start, withGrantOption: false, cascade: true };
      }
      continue;
    }
  }
  return { granteeEnd: end, withGrantOption: false, cascade: false };
}

/** Shared pure core. Operates on a byte range; returns the seed contract or
 *  null for non-grant / structurally-incomplete input. */
function parseGrantCore(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): ParsedParkedGrant | null {
  let i = skipWsCommentsLocal(source, start, end, options);
  let action: ParsedParkedGrant['action'];
  const afterGrant = tryConsumeKeyword(source, i, end, options, 'GRANT');
  const afterRevoke = afterGrant === i ? tryConsumeKeyword(source, i, end, options, 'REVOKE') : i;
  if (afterGrant !== i) { action = 'grant'; i = afterGrant; }
  else if (afterRevoke !== i) { action = 'revoke'; i = afterRevoke; }
  else return null;
  i = skipWsCommentsLocal(source, i, end, options);

  let pgsqlGrantOptionFor = false;
  if (action === 'revoke') {
    const g1 = tryConsumeKeyword(source, i, end, options, 'GRANT');
    if (g1 !== i) {
      const o1 = skipWsCommentsLocal(source, g1, end, options);
      const g2 = tryConsumeKeyword(source, o1, end, options, 'OPTION');
      if (g2 !== o1) {
        const f1 = skipWsCommentsLocal(source, g2, end, options);
        const g3 = tryConsumeKeyword(source, f1, end, options, 'FOR');
        if (g3 !== f1) {
          pgsqlGrantOptionFor = true;
          i = skipWsCommentsLocal(source, g3, end, options);
        }
      }
    }
  }

  const keywords = scanTopLevelKeywords(source, i, end, options);
  const toKw = action === 'grant' ? 'TO' : 'FROM';
  const onK = firstKeyword(keywords, 'ON');
  const sepK = firstKeyword(keywords, toKw);
  if (!sepK) return null;

  let privsEnd: number;
  let objSpan: Span | null = null;
  let granteeStart: number;
  if (onK && onK.start < sepK.start) {
    privsEnd = onK.start;
    objSpan = { s: onK.end, e: sepK.start };
    granteeStart = sepK.end;
  } else {
    privsEnd = sepK.start;
    granteeStart = sepK.end;
  }

  const privileges = splitListByTopCommas(source, i, privsEnd, options)
    .map((seg) => normalizePrivilege(source.slice(seg.s, seg.e)))
    .filter((p) => p.length > 0);

  const tailKws = scanTopLevelKeywords(source, granteeStart, end, options);
  const { granteeEnd, withGrantOption, cascade } = findTrailingOption(
    source,
    tailKws,
    granteeStart,
    end,
    options,
  );

  const grantees = splitListByTopCommas(source, granteeStart, granteeEnd, options)
    .map((seg) => parsePrincipal(source, seg.s, seg.e, options))
    .filter((p): p is ParsedGrantPrincipal => p !== null);

  return {
    action,
    privileges,
    object: objSpan ? parseObject(source, objSpan.s, objSpan.e, options) : null,
    grantees,
    ...(withGrantOption ? { withGrantOption: true } : {}),
    ...(cascade ? { cascade: true } : {}),
    ...(pgsqlGrantOptionFor ? { pgsqlGrantOptionFor: true } : {}),
  };
}

/** Buffer-entry seed helper consumed by the inline `[Edit Privileges]`
 *  popover. Returns the parked-grant seed or null on non-grant / malformed. */
export function extractParkedGrant(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): ParsedParkedGrant | null {
  const { source, options } = buffer.parsedDocument;
  return parseGrantCore(
    source,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
    options,
  );
}

/** Thin string-entry shell (tests + engine-agnostic callers). */
export function extractParkedGrantFromString(
  stmtText: string,
  options: ParseOptions = STRING_SHELL_OPTIONS,
): ParsedParkedGrant | null {
  return parseGrantCore(stmtText, 0, stmtText.length, options);
}

/** DSE2 arm — `extractDdlStructure` dispatches GRANT/REVOKE here. */
export function extractGrant(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const parsed = extractParkedGrant(buffer, statementInfo);
  const kind = parsed?.action === 'revoke' ? 'revoke' : 'grant';
  return { kind, range: statementInfo.range, parsed };
}
